import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Calendar, AlertCircle, User, Grid, List, Camera, UserCircle, FileText, FileCheck, Circle, Clock, CheckCircle, XCircle } from 'lucide-react';

const Akten = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // Arbeitsschritt-Optionen
  const arbeitsschrittOptions = [
    { value: 'ERSTELT', label: 'Erstellt', color: 'bg-red-500 text-white' },
    { value: 'KALKULIERT', label: 'Kalkuliert', color: 'bg-yellow-500 text-white' },
    { value: 'VERSENDET_A', label: 'Versendet/A', color: 'bg-blue-500 text-white' },
    { value: 'VERSENDET_V', label: 'Versendet/V', color: 'bg-blue-500 text-white' },
    { value: 'VERSENDET_W', label: 'Versendet/W', color: 'bg-blue-500 text-white' },
    { value: 'BEZAHLT', label: 'Bezahlt', color: 'bg-green-500 text-white' }
  ];

  // Status-Icon-Mapping
  const getStatusIcon = (status) => {
    switch(status) {
      case 'OFFEN':
        return <Circle className="w-5 h-5 text-orange-500" />;
      case 'IN_BEARBEITUNG':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'ABGESCHLOSSEN':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'STORNIERT':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  // Status-Label für Tooltip
  const getStatusLabel = (status) => {
    switch(status) {
      case 'OFFEN': return 'Offen';
      case 'IN_BEARBEITUNG': return 'In Bearbeitung';
      case 'ABGESCHLOSSEN': return 'Abgeschlossen';
      case 'STORNIERT': return 'Storniert';
      default: return 'Unbekannt';
    }
  };

  // Akten aus Datenbank laden
  const [akten, setAkten] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lade Akten beim Mount
  React.useEffect(() => {
    const ladeAkten = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/akten/liste`);
        
        if (response.ok) {
          const data = await response.json();
          setAkten(data.akten || []);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Akten:', error);
      } finally {
        setLoading(false);
      }
    };

    ladeAkten();
  }, []);

  // Wenn keine echten Akten vorhanden sind, zeige leere Liste
  // (Mock-Daten wurden entfernt)
  
  /*
  // Mock data für Akten (Fallback - entfernt, da wir echte Daten verwenden)
  const mockAkten = [
    {
      id: 1,
      aktenzeichen: 'KH-091125-576',
      typ: 'Haftpflichtgutachten',
      arbeitsschritt: 'ERSTELT',
      status: 'OFFEN',
      kennzeichen: '',
      hersteller: '',
      wert: '',
      created_at: '2025-11-09T17:21:00',
      updated_at: '2025-11-09T17:57:00',
      bearbeiter: 'User',
      thumbnail: null
    },
    {
      id: 2,
      aktenzeichen: 'KH-081125-575',
      typ: 'Haftpflichtgutachten',
      arbeitsschritt: 'KALKULIERT',
      status: 'IN_BEARBEITUNG',
      kennzeichen: '',
      hersteller: '',
      wert: '',
      erstelltAm: '8. Nov. 2025 09:27',
      bearbeitetAm: '8. Nov. 2025 11:29',
      bearbeiter: 'User',
      thumbnail: null
    },
    {
      id: 3,
      aktenzeichen: 'KH-071125-029 Qualid EL Kadiri',
      typ: 'Haftpflichtgutachten',
      arbeitsschritt: 'VERSENDET_A',
      status: 'IN_BEARBEITUNG',
      kennzeichen: 'UN LX 1607',
      hersteller: 'MINI',
      modell: 'Countryman (R60)(2010 > 2016)',
      wert: '6.900',
      erstelltAm: '7. Nov. 2025 16:15',
      bearbeitetAm: '7. Nov. 2025 18:36',
      bearbeiter: 'User',
      thumbnail: '/api/placeholder/80/60'
    },
    {
      id: 4,
      aktenzeichen: 'KH-291025-059 Ouafa Ouyachou',
      typ: 'Haftpflichtgutachten',
      partner: 'UEZ4 Partner',
      arbeitsschritt: 'VERSENDET_V',
      status: 'ABGESCHLOSSEN',
      kennzeichen: 'F GQ 467',
      hersteller: 'Mercedes-Benz',
      modell: 'CLA -Klasse Shooting Brake (BM 117)(12.2014 >)',
      wert: '15.350',
      erstelltAm: '7. Nov. 2025 15:47',
      bearbeitetAm: '7. Nov. 2025 18:31',
      bearbeiter: 'User',
      thumbnail: '/api/placeholder/80/60'
    },
    {
      id: 5,
      aktenzeichen: 'KH-LE 071125 Lara Margarethe Eman',
      typ: 'Haftpflichtgutachten',
      arbeitsschritt: 'BEZAHLT',
      status: 'ABGESCHLOSSEN',
      kennzeichen: 'DN XN 84',
      hersteller: 'Ford',
      modell: 'Fiesta (CBK)(2002 >)',
      wert: '1.100',
      erstelltAm: '7. Nov. 2025 15:23',
      bearbeitetAm: '7. Nov. 2025 16:31',
      bearbeiter: 'User',
      thumbnail: '/api/placeholder/80/60'
    }
  ];
  */

  // Handler für Arbeitsschritt-Änderung
  const handleArbeitsschrittChange = async (akteId, newArbeitsschritt) => {
    try {
      // Update im State
      setAkten(prevAkten => 
        prevAkten.map(akte => 
          akte.akte_id === akteId ? { ...akte, arbeitsschritt: newArbeitsschritt } : akte
        )
      );
      
      // Update im Backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/akten/aktualisieren/${akteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ arbeitsschritt: newArbeitsschritt })
      });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Arbeitsschritts:', error);
    }
  };

  // Hilfsfunktion: Hole Farbe für Arbeitsschritt
  const getArbeitsschrittColor = (arbeitsschritt) => {
    const config = arbeitsschrittOptions.find(opt => opt.value === arbeitsschritt);
    return config ? config.color : 'bg-gray-500 text-white';
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <button onClick={() => navigate('/dashboard')} className="hover:text-blue-600">
            🏠 Start
          </button>
          <span>/</span>
          <span className="text-gray-900 font-semibold">Akten</span>
        </div>

        {/* Filter Section */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Row 1 */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input 
                  placeholder="Suche..." 
                  className="pl-10"
                />
              </div>

              <div>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>🔍 Zugewiesen an</option>
                  <option>Alle Benutzer</option>
                  <option>Aktueller Benutzer</option>
                </select>
              </div>

              <div className="flex gap-2">
                <Input 
                  type="date" 
                  placeholder="Auftragsdatum Von"
                  className="flex-1 text-sm"
                />
                <Input 
                  type="date" 
                  placeholder="Auftragsdatum Bis"
                  className="flex-1 text-sm"
                />
              </div>

              {/* Row 2 */}
              <div>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option>Auftragstyp</option>
                  <option>Haftpflichtgutachten</option>
                  <option>Kaskogutachten</option>
                  <option>Wertgutachten</option>
                </select>
              </div>

              <div>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option>Status</option>
                  <option>Neu</option>
                  <option>In Bearbeitung</option>
                  <option>Erstellt</option>
                  <option>Versendet</option>
                </select>
              </div>

              <div>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option>Arbeitsschritt</option>
                  <option>Besichtigung</option>
                  <option>Kalkulation</option>
                  <option>Begutachtung</option>
                </select>
              </div>

              {/* Row 3 */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input 
                  placeholder="Kontakt/Telefonnummer/Straße" 
                  className="pl-10 text-sm"
                />
              </div>

              <div>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option>Niederlassung</option>
                  <option>Hauptsitz</option>
                  <option>Filiale Nord</option>
                  <option>Filiale Süd</option>
                </select>
              </div>

              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option>🔍 Vermittelt durch</option>
                    <option>Direktkunde</option>
                    <option>Versicherung</option>
                    <option>Anwalt</option>
                  </select>
                </div>
                <Button 
                  variant="outline" 
                  className="whitespace-nowrap text-sm"
                >
                  Fälligkeit überschritten
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              className="text-sm"
            >
              🔘 Erstellt an ▼
            </Button>
            <div className="flex gap-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Akten Tabelle */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Foto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Kunde / Aktenzeichen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Arbeitsschritt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Amtl. Kfz.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Hersteller
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Werte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Erstellt am
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Bearbeiter
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {akten.map((akte) => (
                  <tr 
                    key={akte.id || akte.akte_id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      // Navigiere zur Bearbeitung mit akte_id
                      navigate(`/neue-akte/${akte.akte_id}`);
                    }}
                  >
                    {/* Foto Column - Now First */}
                    <td className="px-6 py-4">
                      {akte.thumbnail ? (
                        <img 
                          src={akte.thumbnail} 
                          alt="Hauptfoto"
                          className="w-16 h-16 rounded object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <Camera className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </td>
                    {/* Kunde / Aktenzeichen Column */}
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        {/* Kundenname mit Icon */}
                        {akte.kunde && (
                          <div className="flex items-center space-x-2">
                            <UserCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <p className="text-sm font-semibold text-gray-900">
                              {akte.kunde}
                            </p>
                          </div>
                        )}
                        {/* Aktenzeichen mit Icon */}
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <p className="text-sm font-medium text-gray-700">
                            {akte.aktenzeichen}
                          </p>
                        </div>
                        {/* Gutachtentyp mit Icon - immer anzeigen */}
                        <div className="flex items-center space-x-2">
                          <FileCheck className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <p className="text-xs text-gray-500">
                            {akte.gutachtentyp || 'Haftpflichtgutachten'}
                          </p>
                        </div>
                        {/* Partner Info */}
                        {akte.partner && (
                          <p className="text-xs text-blue-600 ml-6">{akte.partner}</p>
                        )}
                      </div>
                    </td>
                    {/* Arbeitsschritt Column */}
                    <td className="px-6 py-4">
                      <select
                        value={akte.arbeitsschritt || 'ERSTELT'}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleArbeitsschrittChange(akte.akte_id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-40 px-3 py-2 text-xs font-semibold rounded cursor-pointer border-none outline-none ${getArbeitsschrittColor(akte.arbeitsschritt || 'ERSTELT')}`}
                      >
                        {arbeitsschrittOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {akte.kennzeichen || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {akte.hersteller && (
                        <div>
                          <p className="font-medium">{akte.hersteller}</p>
                          <p className="text-xs text-gray-500">{akte.modell}</p>
                        </div>
                      )}
                      {!akte.hersteller && '-'}
                    </td>
                    <td className="px-6 py-4">
                      {akte.wert && (
                        <div className="text-sm">
                          <p className="text-gray-600">💰 {akte.wert}</p>
                        </div>
                      )}
                      {!akte.wert && '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="space-y-1">
                        <p className="flex items-center space-x-1">
                          <span>🕐</span>
                          <span>{akte.erstelltAm}</span>
                        </p>
                        <p className="flex items-center space-x-1">
                          <span>✏️</span>
                          <span>{akte.bearbeitetAm}</span>
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                      </div>
                    </td>
                    {/* Status Icon Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center" title={getStatusLabel(akte.status || 'OFFEN')}>
                        {getStatusIcon(akte.status || 'OFFEN')}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Akten;
